import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatRecycleRows,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { catchError, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganisationsService } from '../../services/organisations.service';
import { NewOrganisationPick, OrganisationRow } from '../../models/organisations';
import { inactiveDisplay } from '../../utils/organisations.util';
import { AddOrganisationPopoverComponent } from '../add-organisation-popover/add-organisation-popover.component';
import {
  ORGANISATION_ROW_HEIGHT,
  OrganisationsVirtualScrollDataSource
} from '../../services/organisations-virtual-scroll.data-source';
import { ActionButtonsSkeletonComponent } from '../../../users/components/action-buttons-skeleton/action-buttons-skeleton.component';
import { TableSkeletonComponent } from '../../../users/components/table-skeleton/table-skeleton.component';

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

// Maps a mat-sort-header id (the header div's own name, since there's no <th> anymore — see the
// template) to the OrganisationRow field it actually sorts by.
const SORT_FIELDS: Record<string, keyof OrganisationRow> = {
  organisation: 'partyName',
  partyNumber: 'partyNumber',
  activeUsers: 'activeUsersCount',
  activeDepartments: 'activeDepartmentsCount',
  inactiveCount: 'inactiveCount'
};

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return nameCollator.compare(String(a), String(b));
}

// Fetches every assigned organisation once and renders it with true CDK row virtualization,
// mirroring the Contacts grid — see ../../../contacts/VIRTUAL_SCROLL.md for the full rationale
// (custom DataSource, header-outside-viewport sticky-header fix, fixed row height, etc.). Search
// and sort both run client-side against the one in-memory array; there is no per-keystroke or
// per-sort server round-trip.
@Component({
  selector: 'app-organisations-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatCell,
    MatCellDef,
    MatRow,
    MatRowDef,
    MatRecycleRows,
    MatIcon,
    MatProgressSpinner,
    ScrollingModule,
    AddOrganisationPopoverComponent,
    ActionButtonsSkeletonComponent,
    TableSkeletonComponent
  ],
  templateUrl: './organisations-list.component.html',
  styleUrl: './organisations-list.component.scss'
})
export class OrganisationsListComponent implements OnInit {
  @ViewChild(MatSort) matSort!: MatSort;
  @ViewChild('headerRow') headerRow!: ElementRef<HTMLElement>;
  @ViewChild('addOrgButton') addOrgButton!: ElementRef<HTMLButtonElement>;

  @ViewChild(CdkVirtualScrollViewport)
  set virtualScrollViewport(viewport: CdkVirtualScrollViewport | undefined) {
    if (viewport && !this.viewport) {
      this.viewport = viewport;
      this.dataSource.attachViewport(viewport);
      queueMicrotask(() => viewport.checkViewportSize());
    }
  }

  // Restores the search box's text across a list → detail → list round trip, since @if destroys
  // and recreates this component on every view switch (see OrganisationsComponent.view) — without
  // this, reopening the list would silently drop whatever the admin had typed.
  @Input() initialSearchTerm = '';

  @Output() openOrganisation = new EventEmitter<string>();
  @Output() organisationCreated = new EventEmitter<NewOrganisationPick>();
  // Feeds the app-admin-tabs badge on the parent OrganisationsComponent — the true unfiltered
  // total, emitted once per fetch (not the client-filtered totalCount below, which the search box
  // affects — see ContactsComponent.allContactsCount for the same distinction).
  @Output() countChanged = new EventEmitter<number>();
  // Lets the parent hold onto the current search text across this component's destroy/recreate
  // cycle — see initialSearchTerm above.
  @Output() searchTermChanged = new EventEmitter<string>();

  readonly displayedColumns = ['organisation', 'partyNumber', 'activeUsers', 'activeDepartments', 'inactiveCount'];
  readonly inactiveDisplay = inactiveDisplay;
  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly rowHeight = ORGANISATION_ROW_HEIGHT;
  readonly dataSource = new OrganisationsVirtualScrollDataSource(inject(NgZone));

  isLoading = true;
  isTableInitiating = true;
  hasError = false;
  totalCount = 0;
  addOrgOpen = false;
  // Viewport-relative (position: fixed) coordinates for the popover, computed fresh on open — see
  // toggleAddOrg() below for why this can't just be CSS `top: 100%; right: 0` anchored to the
  // button anymore.
  addOrgAnchor = { top: 0, right: 0 };

  private organisationsService = inject(OrganisationsService);
  private destroyRef = inject(DestroyRef);

  private allOrganisations: OrganisationRow[] = [];
  private searchTerm = '';
  private sortState: Sort = { active: '', direction: '' };
  private viewport: CdkVirtualScrollViewport | null = null;

  ngOnInit() {
    if (this.initialSearchTerm) {
      this.searchControl.setValue(this.initialSearchTerm, { emitEvent: false });
      this.searchTerm = this.initialSearchTerm.trim().toLowerCase();
    }

    this.loadOrganisations();

    // Client-side now (the whole set is already in memory), so this filters instantly on every
    // keystroke — no debounce/minimum-length gate, unlike the server-backed search this replaced.
    this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.searchTerm = value.trim().toLowerCase();
      this.searchTermChanged.emit(value);
      this.applyFilterAndSort();
    });
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  onSort(sort: Sort) {
    this.sortState = sort;
    this.applyFilterAndSort();
  }

  // The header row lives outside the CDK viewport (see the template comment for why), so it
  // doesn't get horizontal scroll for free — mirror the viewport's scrollLeft onto it here.
  onViewportScroll(event: Event) {
    this.headerRow.nativeElement.scrollLeft = (event.target as HTMLElement).scrollLeft;
  }

  openRow(row: OrganisationRow) {
    this.openOrganisation.emit(row.partyNumber);
  }

  // Re-fetches the full set — used after a save on the Detail screen brings a new or
  // newly-emptied organisation back into (or out of) the list.
  reload() {
    this.loadOrganisations();
  }

  onPartyPicked(pick: NewOrganisationPick) {
    this.addOrgOpen = false;
    this.organisationCreated.emit(pick);
  }

  // DEV-1213: the Add organisation button now lives inside the horizontally-scrollable toolbar
  // group (so it stays reachable via the same scrollbar the search box uses on narrow screens)
  // instead of sitting outside it. That means its popover can no longer be a plain `position:
  // absolute; top: 100%; right: 0` anchored to the button — an absolutely-positioned descendant
  // still gets visually clipped to the padding-box of any ancestor with overflow other than
  // visible, even where it doesn't affect that ancestor's own scrollable size, and this toolbar
  // group has overflow-x: auto. Instead the popover is `position: fixed`, anchored via
  // getBoundingClientRect() computed here at open time (viewport coordinates, unaffected by
  // ancestor overflow/clipping) — see onToolbarScroll() below for keeping it from going stale.
  toggleAddOrg() {
    if (this.addOrgOpen) {
      this.addOrgOpen = false;
      return;
    }
    const rect = this.addOrgButton.nativeElement.getBoundingClientRect();
    this.addOrgAnchor = { top: rect.bottom + 6, right: window.innerWidth - rect.right };
    this.addOrgOpen = true;
  }

  // The popover is anchored to a snapshot of the button's position (see toggleAddOrg()), so it
  // goes stale the moment the toolbar scrolls the button somewhere else — closing it here matches
  // how a native dropdown/mat-menu behaves when its anchor scrolls out of view.
  onToolbarScroll() {
    this.addOrgOpen = false;
  }

  private loadOrganisations() {
    this.isLoading = true;
    this.hasError = false;
    this.organisationsService
      .getAssigned()
      .pipe(
        catchError(() => {
          this.hasError = true;
          return of<OrganisationRow[]>([]);
        }),
        tap(rows => {
          this.allOrganisations = rows;
          this.countChanged.emit(rows.length);
          this.applyFilterAndSort();
          this.isTableInitiating = false;
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.prefetchUnassigned();
  }

  // Kicks off OrganisationsService's cached unassigned-parties fetch (the ~3.1MB "Add
  // organisation" popover list) in parallel with the assigned-organisations fetch above, instead
  // of waiting for the admin to open the popover. The virtual-scrolled render is fast either way —
  // it's the download itself that's slow on a poor connection, so starting it here buys back that
  // time.
  //
  // Every loadOrganisations() call — including reload(), which runs after a save moves a party
  // between the assigned/unassigned pools — invalidates the cache first, so this is always a fresh
  // fetch, never a stale one from before that save. Errors are swallowed here: the popover's own
  // subscribe still surfaces its "Unable to load parties" state, and OrganisationsService clears
  // its cache on failure so that subscribe retries.
  private prefetchUnassigned() {
    this.organisationsService.invalidateUnassignedCache();
    this.organisationsService
      .getUnassigned()
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private applyFilterAndSort() {
    const term = this.searchTerm;
    const filtered = term
      ? this.allOrganisations.filter(
          row => row.partyName.toLowerCase().includes(term) || row.partyNumber.toLowerCase().includes(term)
        )
      : this.allOrganisations;

    const { active, direction } = this.sortState;
    const field = SORT_FIELDS[active];
    const sorted =
      direction && field
        ? [...filtered].sort((a, b) => {
            const cmp = compareValues(a[field], b[field]);
            return direction === 'asc' ? cmp : -cmp;
          })
        : filtered;

    this.totalCount = sorted.length;
    this.dataSource.setData(sorted);
    this.viewport?.scrollToOffset(0);
  }
}
